import { z } from 'zod';
import { UploadTarget } from './files.js';
import { PersonRef } from './people.js';
import { EMAIL_WIDTH, PRINT_PAGE_SIZES, PrintSpec, PrintSpecInput, UpdatePrintSpecInput, mmToPx } from './showcase-print.js';
/**
 * Chapsi Showcase (docs/adr/0065-showcase.md): design sets on a project, their screens, hotspots and task links.
 * Phase A: no sharing, comments or approvals yet; the shapes leave room for them.
 */
/** Print (brochures, flyers, posters) and email (EDMs) sets arrived with SC-19 (docs/adr/0072). */
export const DesignSetKind = z.enum(['website', 'mobile', 'tablet', 'print', 'email']);
export const CreatableDesignSetKind = z.enum(['website', 'mobile', 'tablet', 'print', 'email']);
/** Screens (website, mobile, tablet) have device sizes; print sets a page size (A4, A5, DL); email sets 600 px. */
export const DevicePreset = z.enum(['desktop_1440', 'iphone', 'android', 'ipad', 'a4', 'a5', 'dl', 'email', 'custom']);
export const DEVICE_PRESETS = {
    desktop_1440: { label: 'Desktop', width: 1440, height: 900, kind: 'website' },
    iphone: { label: 'iPhone', width: 390, height: 844, kind: 'mobile' },
    android: { label: 'Android', width: 412, height: 915, kind: 'mobile' },
    ipad: { label: 'iPad', width: 1194, height: 834, kind: 'tablet' },
    // A print preset's size is one portrait page at 96 dpi; the set's own size follows its orientation and layout.
    a4: { label: PRINT_PAGE_SIZES.a4.label, width: Math.round(mmToPx(PRINT_PAGE_SIZES.a4.widthMm)), height: Math.round(mmToPx(PRINT_PAGE_SIZES.a4.heightMm)), kind: 'print' },
    a5: { label: PRINT_PAGE_SIZES.a5.label, width: Math.round(mmToPx(PRINT_PAGE_SIZES.a5.widthMm)), height: Math.round(mmToPx(PRINT_PAGE_SIZES.a5.heightMm)), kind: 'print' },
    dl: { label: PRINT_PAGE_SIZES.dl.label, width: Math.round(mmToPx(PRINT_PAGE_SIZES.dl.widthMm)), height: Math.round(mmToPx(PRINT_PAGE_SIZES.dl.heightMm)), kind: 'print' },
    email: { label: 'Email', width: EMAIL_WIDTH, height: 900, kind: 'email' },
};
/** The preset a new set of this kind starts with. */
export function defaultPresetFor(kind) {
    return kind === 'website' ? 'desktop_1440' : kind === 'mobile' ? 'iphone' : kind === 'tablet' ? 'ipad' : kind === 'print' ? 'a4' : 'email';
}
/** Screens (website, mobile, tablet) share their device presets; print and email sets have their own. */
const presetFamily = (kind) => (kind === 'print' ? 'print' : kind === 'email' ? 'email' : 'screen');
/** The presets a set of this kind may use (custom is always allowed), the kind's own first. */
export function presetsFor(kind) {
    const all = Object.keys(DEVICE_PRESETS);
    return all.filter((p) => presetFamily(DEVICE_PRESETS[p].kind) === presetFamily(kind)).sort((a, b) => (DEVICE_PRESETS[a].kind === kind ? 0 : 1) - (DEVICE_PRESETS[b].kind === kind ? 0 : 1));
}
export function presetSuits(kind, preset) {
    return preset === 'custom' || presetsFor(kind).includes(preset);
}
export const DESIGN_KIND_LABELS = {
    website: 'Website',
    mobile: 'Mobile app',
    tablet: 'Tablet app',
    print: 'Print',
    email: 'Email',
};
export const DESIGN_DEVICE_MIN = 200;
export const DESIGN_DEVICE_MAX = 4000;
/**
 * Files accepted as screens (docs/adr/0065). A PDF becomes one PNG screen per page once the worker has split it;
 * Figma import comes later.
 */
export const DESIGN_SCREEN_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];
export const DesignScreenType = z.enum(DESIGN_SCREEN_TYPES);
/** Pages read from one PDF; the rest are not turned into screens (the upload says so). */
export const MAX_PDF_PAGES = 50;
export const MAX_DESIGN_SCREEN_MB = 30;
export const MAX_SCREENS_PER_UPLOAD = 30;
export const MAX_SCREENS_PER_VERSION = 200;
export const MAX_SCREEN_WIDTH = 8000;
export const MAX_SCREEN_HEIGHT = 32000;
export const MAX_SCREEN_PIXELS = 120_000_000;
/** Smallest hotspot side, as a fraction of the screen. */
export const MIN_HOTSPOT_SIDE = 0.005;
export const DesignSetStatus = z.enum(['draft', 'in_review', 'changes_requested', 'approved']);
export const DESIGN_STATUS_LABELS = {
    draft: 'Draft',
    in_review: 'In review',
    changes_requested: 'Changes requested',
    approved: 'Approved',
};
/** `processing`: an uploaded PDF waiting to be split into screens by the worker. */
export const ScreenUploadStatus = z.enum(['uploading', 'processing', 'ready', 'failed']);
export const ScreenScanStatus = z.enum(['pending', 'clean', 'infected', 'skipped']);
/** A screen can be shown, linked and played once it arrived and its scan allows it (docs/adr/0065). */
export function screenUsable(s) {
    return s.status === 'ready' && (s.scan === 'clean' || s.scan === 'skipped');
}
export const HotspotTargetKind = z.enum(['none', 'screen', 'back', 'url', 'overlay']);
export const HotspotTransition = z.enum(['instant', 'slide_left', 'slide_right', 'slide_up', 'dissolve', 'push']);
export const TRANSITION_LABELS = {
    instant: 'Instant',
    slide_left: 'Slide left',
    slide_right: 'Slide right',
    slide_up: 'Slide up',
    dissolve: 'Dissolve',
    push: 'Push',
};
export const DesignScreen = z.object({
    id: z.uuid(),
    position: z.number().int(),
    title: z.string(),
    fileName: z.string(),
    contentType: DesignScreenType,
    size: z.number().int(),
    /** Measured from the file on the server. Null until the upload completes. */
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    status: ScreenUploadStatus,
    scan: ScreenScanStatus,
    usable: z.boolean(),
    /** A signed link that lasts about 5 minutes (see `urlsExpireAt`). Null unless the screen is usable. */
    url: z.string().nullable(),
    /** The worker's small WebP of the top of the screen; null until made (then scale the original). */
    thumbnailUrl: z.string().nullable(),
    /** `pending` until the worker has made the thumbnail and tiles; `none` for PDFs, which become other screens. */
    media: z.enum(['pending', 'ready', 'failed', 'none']),
    /**
     * The screen as WebP tiles at display size, top to bottom, with `top` and `height` as fractions of the screen's
     * height. Null until made: show `url` (the original) instead.
     */
    tiles: z.array(z.object({ url: z.string(), top: z.number(), height: z.number() })).nullable(),
    /** The page of the PDF this screen came from. */
    pageNumber: z.number().int().nullable(),
    /** `placeholder`: a stand-in Chapi added for a screen that looked missing (docs/adr/0069). */
    source: z.enum(['upload', 'pdf_page', 'figma', 'placeholder']),
    /** Against the previous version (docs/adr/0069): Same, Changed or New; null in version 1. */
    change: z.enum(['same', 'changed', 'new']).nullable().optional(),
    /** A PDF page's size as printed (docs/adr/0072), measured from the PDF; null for images. */
    pageSizeMm: z.object({ width: z.number(), height: z.number() }).nullable().optional(),
    createdAt: z.string(),
});
export const Hotspot = z.object({
    id: z.uuid(),
    screenId: z.uuid(),
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
    targetKind: HotspotTargetKind,
    targetScreenId: z.uuid().nullable(),
    targetUrl: z.string().nullable(),
    transition: HotspotTransition,
    fixed: z.boolean(),
    source: z.enum(['manual', 'chapi']),
    confidence: z.enum(['high', 'medium', 'low']).nullable(),
    label: z.string().nullable(),
});
export const DesignSetSummary = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    projectName: z.string(),
    name: z.string(),
    kind: DesignSetKind,
    devicePreset: DevicePreset,
    deviceWidth: z.number().int(),
    deviceHeight: z.number().int(),
    status: DesignSetStatus,
    visibility: z.enum(['internal', 'shared']),
    currentVersion: z.number().int(),
    /** The latest version clients see (docs/adr/0069); lower than `currentVersion` while a new version is a draft. */
    publishedVersion: z.number().int(),
    startScreenId: z.uuid().nullable(),
    /** A print set's page size, layout and proof margins (docs/adr/0072); null for every other kind. */
    print: PrintSpec.nullable().optional(),
    /** Usable screens in the current version (uploads in flight are not counted). */
    screenCount: z.number().int(),
    /** Hotspots with a destination. */
    linkCount: z.number().int(),
    /** Up to three of the first usable screens, for the card. */
    covers: z.array(z.object({ screenId: z.uuid(), url: z.string(), width: z.number().int(), height: z.number().int() })),
    createdBy: PersonRef.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const DesignSetList = z.object({
    items: z.array(DesignSetSummary),
    /** When the signed links in this response stop working; refetch before then. */
    urlsExpireAt: z.string(),
});
export const DesignVersion = z.object({
    id: z.uuid(),
    number: z.number().int(),
    createdAt: z.string(),
    lockedAt: z.string().nullable(),
    /** Null while a new version is a draft only the team sees (docs/adr/0069). */
    publishedAt: z.string().nullable().optional(),
    /** Why a version after an approval was uploaded. */
    reason: z.string().nullable().optional(),
});
export const DesignSetDetail = z.object({
    set: DesignSetSummary,
    clientName: z.string(),
    version: DesignVersion,
    /** Every screen of the current version, in order, including uploads in flight and failed ones. */
    screens: z.array(DesignScreen),
    hotspots: z.array(Hotspot),
    /** The person may change the set (not a locked version, not an older version). */
    canEdit: z.boolean(),
    /** The version shown is the latest one (older versions open read-only, docs/adr/0069). */
    isLatest: z.boolean().optional(),
    urlsExpireAt: z.string(),
});
const SetName = z.string().trim().min(1, 'Give the set a name').max(120);
const DeviceSide = z.number().int().min(DESIGN_DEVICE_MIN).max(DESIGN_DEVICE_MAX);
export const CreateDesignSetInput = z
    .object({
    projectId: z.uuid(),
    name: SetName,
    kind: CreatableDesignSetKind,
    devicePreset: DevicePreset,
    /** Custom size only; presets use their own. A custom print size is given in millimetres in `print` instead. */
    deviceWidth: DeviceSide.optional(),
    deviceHeight: DeviceSide.optional(),
    /** Print sets: layout, orientation, custom trim and proof margins (docs/adr/0072). */
    print: PrintSpecInput.optional(),
})
    .refine((v) => presetSuits(v.kind, v.devicePreset), { message: 'Choose a size that suits this kind of set', path: ['devicePreset'] })
    .refine((v) => v.kind === 'print' || v.devicePreset !== 'custom' || (v.deviceWidth !== undefined && v.deviceHeight !== undefined), {
    message: 'Give a width and height for a custom size',
    path: ['deviceWidth'],
})
    .refine((v) => v.kind !== 'print' || v.devicePreset !== 'custom' || (v.print?.trimWidthMm !== undefined && v.print?.trimHeightMm !== undefined), {
    message: 'Give the page width and height in millimetres',
    path: ['print', 'trimWidthMm'],
})
    .refine((v) => v.kind === 'print' || v.print === undefined, { message: 'Only print sets have a page size', path: ['print'] });
export const UpdateDesignSetInput = z
    .object({
    name: SetName.optional(),
    devicePreset: DevicePreset.optional(),
    deviceWidth: DeviceSide.optional(),
    deviceHeight: DeviceSide.optional(),
    startScreenId: z.uuid().nullable().optional(),
    /** Print sets: layout and proof margins (docs/adr/0072). */
    print: UpdatePrintSpecInput.optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change' })
    .refine((v) => v.devicePreset !== 'custom' || (v.deviceWidth !== undefined && v.deviceHeight !== undefined), {
    message: 'Give a width and height for a custom size',
    path: ['deviceWidth'],
});
/** An older version to open read-only (docs/adr/0069); omit for the latest. */
export const GetDesignSetQuery = z.object({ version: z.coerce.number().int().min(1).max(10_000).optional() });
export const ListDesignSetsQuery = z.object({
    /** Comma-separated project ids (a client channel's projects); omit for every project the person can see. */
    projects: z
        .string()
        .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(,[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}){0,49}$/i, 'Unknown projects')
        .optional(),
});
export const AddDesignScreensInput = z.object({
    files: z
        .array(z.object({
        name: z.string().trim().min(1).max(255),
        size: z.number().int().min(1),
        contentType: DesignScreenType,
    }))
        .min(1)
        .max(MAX_SCREENS_PER_UPLOAD),
});
export const AddedDesignScreens = z.object({ items: z.array(z.object({ screen: DesignScreen, upload: UploadTarget })) });
export const UpdateDesignScreenInput = z.object({ title: z.string().trim().min(1, 'Give the screen a name').max(120) });
export const ReorderDesignScreensInput = z.object({ screenIds: z.array(z.uuid()).min(1).max(MAX_SCREENS_PER_VERSION) });
const Fraction = z.number().min(0).max(1);
const Side = z.number().min(MIN_HOTSPOT_SIDE).max(1);
const HttpsUrl = z.string().trim().max(2000).pipe(z.url({ protocol: /^https$/, error: 'Use a full https:// link' }));
const fits = (v) => (v.x === undefined || v.w === undefined || v.x + v.w <= 1.000001) && (v.y === undefined || v.h === undefined || v.y + v.h <= 1.000001);
export const CreateHotspotInput = z
    .object({
    x: Fraction,
    y: Fraction,
    w: Side,
    h: Side,
    targetKind: HotspotTargetKind.default('none'),
    targetScreenId: z.uuid().nullable().optional(),
    targetUrl: HttpsUrl.nullable().optional(),
    transition: HotspotTransition.default('instant'),
    fixed: z.boolean().default(false),
})
    .refine(fits, { message: 'The area must sit inside the screen' });
export const UpdateHotspotInput = z
    .object({
    x: Fraction.optional(),
    y: Fraction.optional(),
    w: Side.optional(),
    h: Side.optional(),
    targetKind: HotspotTargetKind.optional(),
    targetScreenId: z.uuid().nullable().optional(),
    targetUrl: HttpsUrl.nullable().optional(),
    transition: HotspotTransition.optional(),
    fixed: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to change' })
    .refine(fits, { message: 'The area must sit inside the screen' });
export const DesignActivityKind = z.enum(['set_created', 'screens_uploaded', 'set_renamed', 'start_screen_set', 'screen_removed', 'set_shared', 'comments_added', 'visibility_changed', 'version_created', 'version_published', 'version_approved']);
export const DesignActivity = z.object({
    id: z.uuid(),
    setId: z.uuid(),
    setName: z.string(),
    kind: DesignActivityKind,
    actor: PersonRef.nullable(),
    /** Counts and names: `count` for uploads, `title` for a screen, `name` for the old name of a renamed set. */
    count: z.number().int().nullable(),
    title: z.string().nullable(),
    createdAt: z.string(),
});
export const DesignActivityList = z.object({ items: z.array(DesignActivity) });
export const TaskDesignLink = z.object({
    id: z.uuid(),
    workItemId: z.uuid(),
    setId: z.uuid(),
    setName: z.string(),
    kind: DesignSetKind,
    screenId: z.uuid().nullable(),
    screenTitle: z.string().nullable(),
    /** The linked screen, or the set's first usable screen; a 5-minute signed link. */
    thumbUrl: z.string().nullable(),
});
export const TaskDesignLinkList = z.object({ items: z.array(TaskDesignLink), urlsExpireAt: z.string() });
export const AddTaskDesignLinkInput = z.object({ setId: z.uuid(), screenId: z.uuid().nullable().optional() });
/** "02-booking_details.png" → "Booking details": a screen's starting title from its file name. */
export function titleFromFileName(name) {
    const base = name.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/^[\s\d._-]+(?=[a-z])/i, '');
    const words = base.replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!words)
        return 'Screen';
    const t = words.charAt(0).toUpperCase() + words.slice(1);
    return t.slice(0, 120);
}
//# sourceMappingURL=showcase.js.map